import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Box, Card, ElementContainer, MultiSelectChip, SideDrawer, TextField, withStyles } from 'vopay';
import type { AccountUserRole } from 'vopay';
import { Button } from '#shared/components/UI';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useToast } from '#shared/providers/Toast';
import useUserRoleAssignment from '../useUserRoleAssignment';
import styles from './CreateUserDrawer.module.scss';
import useCreateUser from './useCreateUser';

const cn = withStyles(styles);

type CreateUserDrawerProps = {
  isOpen: boolean;
  existingUsernames: string[];
  availableRoles?: AccountUserRole[];
  onClose: () => void;
  onUserCreated?: () => void;
  /**
   * If provided, the role dropdown shows a "+ New Role" footer button. The callback receives the
   * form's current in-progress state so the parent can preserve it across the Create-Role chain.
   */
  onCreateNewRole?: (currentState: { emailAddress: string; selectedRoleIDs: number[] }) => void;
  /** Applied on each closed→open transition, to restore state after the Create-Role chain. */
  initialEmailAddress?: string;
  initialRoleIDs?: number[];
};

type RoleOption = { value: string; label: string };

export default function CreateUserDrawer({
  isOpen,
  existingUsernames,
  availableRoles = [],
  onClose,
  onUserCreated,
  onCreateNewRole,
  initialEmailAddress,
  initialRoleIDs,
}: CreateUserDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.CreateUserDrawer' });
  const { createUser } = useCreateUser();
  const { assignUserRole } = useUserRoleAssignment();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();
  const [emailAddress, setEmailAddress] = useState('');
  const [selectedRoleIDs, setSelectedRoleIDs] = useState<number[]>([]);

  // Hydrate from the initial props only on a true closed→open transition, so props changing while the
  // drawer is open can't reset the form mid-edit.
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setEmailAddress(initialEmailAddress ?? '');
      setSelectedRoleIDs(initialRoleIDs ?? []);
    }

    wasOpen.current = isOpen;
  }, [isOpen, initialEmailAddress, initialRoleIDs]);

  const isEmailValid = useMemo(() => Yup.string().email().required().isValidSync(emailAddress), [emailAddress]);

  const isDuplicateEmail = useMemo(
    () => existingUsernames.some((existing) => existing.toLowerCase() === emailAddress.toLowerCase()),
    [existingUsernames, emailAddress],
  );

  const roleOptions: RoleOption[] = availableRoles.map((role) => ({
    value: String(role.RoleID),
    label: role.Name,
  }));

  function handleRolesChange(_event: ChangeEvent<HTMLInputElement>, args: { options: RoleOption[] }) {
    setSelectedRoleIDs(args.options.map((opt) => Number(opt.value)));
  }

  function handleRoleDelete(value: string) {
    setSelectedRoleIDs((prev) => prev.filter((id) => String(id) !== value));
  }

  async function handleCreate() {
    blockingLoader.show();

    try {
      await createUser({ EmailAddress: emailAddress, SendEmail: true });

      // Once the user exists, role assignments run in parallel and are tallied so the toast can be
      // honest about a partial failure.
      if (selectedRoleIDs.length > 0) {
        const assignResults = await Promise.allSettled(
          selectedRoleIDs.map((RoleID) => assignUserRole({ Username: emailAddress, RoleID })),
        );

        const numAssigned = assignResults.filter((r) => r.status === 'fulfilled').length;
        const numFailed = assignResults.length - numAssigned;

        if (numFailed === 0) {
          toast.success(
            t('successMessageWithRoles', {
              defaultValue: 'User was successfully created and {{numAssigned}} role(s) assigned.',
              numAssigned,
            }),
          );
        } else if (numAssigned > 0) {
          toast.neutral(
            t('partialRolesAssigned', {
              defaultValue: 'User created. {{numAssigned}} of {{numTotal}} roles assigned ({{numFailed}} failed).',
              numAssigned,
              numTotal: assignResults.length,
              numFailed,
            }),
          );
        } else {
          toast.neutral(t('rolesAssignFailed', { defaultValue: 'User was created, but the role assignments failed.' }));
        }
      } else {
        toast.success(t('successMessage', { defaultValue: 'User was successfully created.' }));
      }

      onUserCreated?.();
      onClose();
    } catch {
      toast.neutral(t('failMessage', { defaultValue: 'Unable to create the user.' }));
    } finally {
      blockingLoader.hide();
    }
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Create User' })}
      footer={
        <Box className={cn('footer')}>
          <Button colorScheme="primary" disabled={!isEmailValid || isDuplicateEmail} onClick={handleCreate}>
            {t('createButton', { defaultValue: 'Create User' })}
          </Button>
        </Box>
      }
    >
      <div className={cn('body')}>
        <Card hoverEffect={false}>
          <Box flx="col" gapSize="16px">
            <ElementContainer
              label={t('emailAddress', { defaultValue: 'Email Address' })}
              required
              isBold={false}
              padding="0 0 6px"
              htmlFor="newUserEmail"
              input={
                <TextField
                  name="newUserEmail"
                  placeholder={t('emailPlaceholder', { defaultValue: 'alex.rogers@acme.com' })}
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  variant="outlineShadow"
                  error={
                    isDuplicateEmail
                      ? t('duplicateEmail', {
                          defaultValue: 'This email is already used. Please use a different email.',
                        })
                      : undefined
                  }
                  errorFitContent
                />
              }
            />

            {availableRoles.length > 0 && (
              <ElementContainer
                label={t('selectRoles', { defaultValue: 'Select Role(s)' })}
                isBold={false}
                padding="0 0 6px"
                htmlFor="newUserRoles"
                input={
                  <MultiSelectChip
                    name="newUserRoles"
                    options={roleOptions}
                    value={selectedRoleIDs.map(String)}
                    onChange={handleRolesChange}
                    onDelete={handleRoleDelete}
                    placeholder={t('selectRolesPlaceholder', { defaultValue: 'Select' })}
                    filterable
                    emptyFilterMsg={t('noRolesFound', { defaultValue: 'No roles found' })}
                    renderOption={(o, { Option, SelectedIcon, selected }) => (
                      <Option>
                        {o.label}
                        {selected && <SelectedIcon />}
                      </Option>
                    )}
                    renderDropdownFooter={
                      onCreateNewRole
                        ? ({ close }) => (
                            <Button
                              iconLeft="add"
                              className={cn('newRoleButton')}
                              onClick={() => {
                                close();
                                onCreateNewRole({ emailAddress, selectedRoleIDs });
                              }}
                            >
                              {t('newRole', { defaultValue: 'New Role' })}
                            </Button>
                          )
                        : undefined
                    }
                  />
                }
              />
            )}
          </Box>
        </Card>
      </div>
    </SideDrawer>
  );
}
